import React, { Suspense, useState } from 'react';
import styled from '@emotion/styled';
import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import Select from '@vromo/ui/components/Select';
import theme from '@vromo/ui/components/theme';
import { css } from 'emotion';
import { Route } from 'react-router';

import ProfileImage from '@vromo/ui/components/ProfileImage';

import LogoButton from './LogoButton';

import DarkMode from './DarkMode';

import Permissions from '@vromo/ui/components/Permissions';

import { DropDown, DropDownLink, DropDownItem } from '@vromo/ui/components/DropDown';

import FontAwesome from '@vromo/ui/components/FontAwesome';

import { UpgradeWrapper } from '../../freemium/UpgradeModal';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { lighten } from 'polished';
import Modal, { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import { distanceInWordsToNow } from 'date-fns';
import timeStore from '@vromo/dashboard/src/stores/TimeStore';
import { isLocalhost } from '@vromo/utils';
import { LocalStorage } from '@vromo/models';
import { qaLabelTopBar } from '../../../../../common-qa-labels/top-bar';

const Integrations = React.lazy(
    () => import(/* webpackPrefetch: true */ './IntegrationToken.lazy')
);

const NavbarContainer = styled.div`
    min-height: 45px;
    margin-bottom: 0 !important;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background-color: white !important;
    flex-shrink: 0;

    border-bottom: 1px solid rgba(151, 151, 151, 0.38);
    box-shadow: 0 2px 4px 0 rgba(233, 233, 233, 0.5);
`;

const RolePicker = observer(function RolePicker({ ...props }) {
    const rootStore = useStore<RootStore>('rootStore');
    const { authStore } = rootStore;

    if (authStore.roles.length <= 1) return null;

    return (
        <Select
            {...props}
            value={authStore.role.id}
            data-testid={qaLabelTopBar.rolePicker}
            onChange={(e) => authStore.changeRole(parseInt(e.target.value))}>
            {authStore.roles
                .sortBy((r) => r.group.name.toLowerCase())
                .map((it) => (
                    <option key={it.id} value={it.id}>
                        {it.group.name} ({it.position.name})
                    </option>
                ))}
        </Select>
    );
});

const ProfileDetails = observer(function ProfileDetails() {
    const {
        authStore: { user, role, roles, isFreemium }
    } = useStore('rootStore');

    return (
        <div
            style={{
                padding: 10,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
            }}>
            <DropDown
                menuStyle={{ width: 250 }}
                button={
                    <div
                        data-testid={qaLabelTopBar.profileMenu}
                        className={css`
                            display: flex;
                            align-items: center;

                            i,
                            div {
                                color: ${theme.grey};
                                transition: color 0.2s ease-in-out;
                            }

                            div {
                                color: ${theme.mediumGrey};
                            }

                            img {
                                margin: 0px 8px;
                                transition: box-shadow 0.2s ease-in-out;
                                box-shadow: 0 0px 0px 0px ${theme.grey};
                            }

                            &:hover {
                                i {
                                    color: ${theme.darkGrey};
                                }

                                div {
                                    color: ${theme.text.dark};
                                }
                                img {
                                    box-shadow: 0 2px 5px 1px ${theme.grey};
                                }
                            }
                        `}
                        style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                        <div>
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 'bold',
                                    textAlign: 'right'
                                }}>
                                {role.group?.name}
                            </div>
                            <div style={{ fontSize: 14, textAlign: 'right' }}>
                                {user.contact.name || user.contact.email}
                            </div>
                        </div>
                        {user.contact.email && (
                            <ProfileImage
                                url={user?.avatar?.url}
                                user={user}
                                size={40}
                                style={{
                                    border: `1px solid ${theme.lightGrey}`,
                                    margin: '0px 5px'
                                }}
                            />
                        )}
                        <FontAwesome
                            icon="caret-down"
                            data-testid={ qaLabelTopBar.profileIcon }
                        />
                    </div>
                }>
                <div>
                    <Route path="/dispatch" render={() => <DarkMode />} />
                    {roles.length > 1 ? (
                        <>
                            <div style={{ padding: '0px 20px' }}>
                                <label style={{ marginTop: '10px' }}>Role</label>
                                <RolePicker />
                            </div>
                            <hr style={{ width: '100%' }} />
                        </>
                    ) : null}
                </div>
                <Suspense fallback={null}>
                    <Permissions minPosition="freemiumAdmin">
                        <Integrations>
                            <DropDownItem>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%'
                                    }}>
                                    <FontAwesome
                                        icon="plug"
                                        weight="regular"
                                        style={{ marginRight: '10px', width: '13px' }}
                                    />
                                    Integrations
                                </div>
                            </DropDownItem>
                        </Integrations>
                    </Permissions>
                </Suspense>
                {isFreemium ? (
                    <UpgradeWrapper isRequest>
                        <DropDownItem>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                <FontAwesome
                                    icon="arrow-circle-up"
                                    weight="regular"
                                    style={{ marginRight: '10px', width: '13px' }}
                                />
                                Upgrade to Premium
                            </div>
                        </DropDownItem>
                    </UpgradeWrapper>
                ) : null}
                <DropDownLink to="/settings">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesome icon="cog" weight="regular" style={{ marginRight: '10px' }} />
                        Account Settings
                    </div>
                </DropDownLink>
                <DropDownItem
                    style={{ display: 'flex', alignItems: 'center' }}
                    href="https://help.vromo.io/knowledge/"
                    target="_blank"
                    rel="noreferrer noopener">
                    <FontAwesome
                        icon="question-circle"
                        weight="regular"
                        style={{ marginRight: '10px' }}
                    />
                    Help and Support
                </DropDownItem>
                {(isLocalhost() || GIT_BRANCH === 'staging') && (
                    <DropDownItem
                        onClick={() => {
                            LocalStorage.isInitialSignup = true;
                            window.location.reload();
                        }}
                        style={{ display: 'flex', alignItems: 'center' }}
                        target="_blank"
                        rel="noreferrer noopener">
                        <FontAwesome
                            icon="question-circle"
                            weight="regular"
                            style={{ marginRight: '10px' }}
                        />
                        Show Signup Survey
                    </DropDownItem>
                )}
                <DropDownLink to="/logout" data-testid={qaLabelTopBar.actionMenu.logOut}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesome
                            icon="sign-out"
                            weight="regular"
                            style={{ marginRight: '10px' }}
                        />
                        Logout
                    </div>
                </DropDownLink>
            </DropDown>
        </div>
    );
});

export function Navbar({ children, toggleSidebar }) {
    return (
        <NavbarContainer>
            <div style={{ display: 'flex', alignItems: 'center', padding: '5px 10px' }}>
                <div
                    onClick={toggleSidebar}
                    className={css`
                        display: none;
                        cursor: pointer;

                        @media (max-width: 1000px) {
                            display: block;
                        }
                    `}>
                    <FontAwesome
                        icon="bars"
                        className={css`
                            color: ${theme.primary};
                            margin-right: 8px;
                        `}
                        large
                    />
                </div>
                <LogoButton />
            </div>
            <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>{children}</div>
        </NavbarContainer>
    );
}

const TrialPeriod = observer(function TrialPeriod() {
    const TRIAL_PERIOD_DURATION = 1209600000; // Two weeks

    const [openTrialModal, setOpenTrialModal] = useState(false);

    const {
        authStore: { user, role, status }
    } = useStore<RootStore>('rootStore');

    const renderExpiryWarning = (expiryDate: number) => (
        <Modal isOpen={openTrialModal} onRequestClose={() => setOpenTrialModal(false)}>
            <ModalHeader
                className={css`
                    text-align: center;
                    margin-top: 20px;
                `}>
                <FontAwesome
                    weight="duotone"
                    icon="arrow-circle-up"
                    className={css`
                        color: ${theme.primary};
                        font-size: 5rem;
                    `}
                />
            </ModalHeader>
            <ModalContent
                className={css`
                    text-align: center;
                    font-size: 1.4em;
                `}>
                <p>
                    Your trial account with VROMO expires in{' '}
                    <strong>{distanceInWordsToNow(expiryDate)}</strong>.
                </p>
                <p>Click below to upgrade your account now.</p>
            </ModalContent>
            <ModalActions>
                <a
                    style={{ margin: 'auto' }}
                    href="https://cta-redirect.hubspot.com/cta/redirect/5979392/a09dee2f-7037-4014-bf99-da7b6c47caa5">
                    <Button large primary style={{ margin: 'auto' }}>
                        Upgrade
                    </Button>
                </a>
            </ModalActions>
        </Modal>
    );

    const renderExpiryModal = () => (
        <Modal isOpen={true}>
            <ModalHeader
                className={css`
                    text-align: center;
                    margin-top: 20px;
                `}>
                <FontAwesome
                    weight="duotone"
                    icon="exclamation-triangle"
                    className={css`
                        color: ${theme.danger};
                        font-size: 5rem;
                    `}
                />
            </ModalHeader>
            <ModalContent
                className={css`
                    text-align: center;
                    font-size: 1.4em;
                `}>
                <strong>Your trial account with VROMO has expired.</strong>
                <p>Click below to upgrade your account now.</p>
            </ModalContent>
            <ModalActions>
                <a
                    style={{ margin: 'auto' }}
                    href="https://cta-redirect.hubspot.com/cta/redirect/5979392/a09dee2f-7037-4014-bf99-da7b6c47caa5">
                    <Button large primary>
                        Upgrade
                    </Button>
                </a>
            </ModalActions>
        </Modal>
    );

    if (status.created && status.type != 'invoiced') {
        const expiryDate = status.created + TRIAL_PERIOD_DURATION;
        const isExpired = expiryDate < timeStore.fastTime;
        return (
            <div>
                <div
                    onClick={() => setOpenTrialModal(true)}
                    className={css`
                        padding: 10px;
                        border-radius: 3px;
                        background-color: #fff;
                        transition: background-color 150ms ease-in-out;
                        color: ${theme.primary};
                        cursor: pointer;

                        &:hover {
                            background-color: ${lighten(0.3, theme.primary)};
                        }
                    `}>
                    {isExpired ? (
                        <strong>Your trial has expired.</strong>
                    ) : (
                        <>
                            Your trial expires in{' '}
                            <strong>{distanceInWordsToNow(expiryDate)}</strong>.
                        </>
                    )}
                </div>
                {isExpired ? renderExpiryModal() : renderExpiryWarning(expiryDate)}
            </div>
        );
    } else {
        return null;
    }
});

export default observer<{ toggleSidebar: () => void }>(function ActionBar({ toggleSidebar }) {
    return (
        <Navbar toggleSidebar={toggleSidebar}>
            <TrialPeriod />
            <ProfileDetails />
        </Navbar>
    );
});
